import { styled, alpha } from '@mui/material/styles';
import { Dialog, FormControlLabel } from '@mui/material';

export const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paperScrollPaper': {
    backgroundColor: theme.palette.background.default,
  },

  '@media print': {
    position: 'relative !important',
    backgroundColor: theme.palette.common.white,
    display: 'block',
    '& .MuiBackdrop-root': {
      display: 'none',
    },
    '& .MuiDialog-scrollPaper': {
      display: 'block',
    },
    '& .MuiDialog-paperScrollPaper': {
      display: 'block',
      height: 'auto !important',
      backgroundColor: theme.palette.common.white,
    },
  },
}));

export const FauxPage = styled('div')(({ theme }) => ({
  margin: '20px auto',
  maxWidth: 1000,
  width: '100%',
  padding: theme.spacing(2),
  boxShadow: theme.shadows[10],
  backgroundColor: theme.palette.background.paper,

  '@media print': {
    margin: 0,
    maxWidth: '100%',
    padding: 0,
    boxShadow: 'none',
  },
}));

export const Title = styled('div')({
  fontSize: 20,
  fontWeight: 'bold',
  marginBottom: 10,
});

export const CoverTitle = styled('div')({
  fontSize: 16,
});

export const ButtonContainer = styled('div', {
  shouldForwardProp: (prop) => !['fixed', 'sticky', 'mobileVariant'].includes(prop),
})(({ theme, fixed, sticky, mobileVariant }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  zIndex: 100,
  ...((fixed || sticky) && { position: 'fixed !important' }),
  ...(fixed && {
    top: theme.spacing(2),
  }),
  ...(sticky &&
    !fixed && {
      backgroundColor: alpha(theme.palette.background.default, 0.95),
      padding: theme.spacing(0.5, 1),
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[2],
    }),

  ...(!fixed && {
    [theme.breakpoints.up('xs')]: {
      top: 75,
    },
    [theme.breakpoints.down('sm')]: {
      top: 65,
    },
  }),

  '& button': {
    marginLeft: theme.spacing(1),
    // Lock the height so MUI v9's default contained-button sizing doesn't make
    // these render taller than the v5 reference (PFM-2599 QA round 1).
    height: 36,
    minHeight: 36,
    lineHeight: 1.5,
    '&:first-child': {
      marginLeft: 0,
    },
  },

  // PFM-2628 (rework): on small screens the Share/Download/Print cluster no
  // longer overlays (or sits below) the assessment tabs. Two opt-in mobile
  // variants drive that, and neither touches the print dialog `fixed`/`sticky`
  // overlays:
  //
  //  - `hideOnMobile`: the in-page Tabs cluster is HIDDEN below `md` (the
  //    desktop top-right overlay is preserved at `md`+). Used by the PrintView
  //    that lives in the tabs AppBar so no buttons appear by/under the tabs on
  //    mobile.
  //  - `headerRow`: the dedicated mobile cluster rendered (via portal) directly
  //    under the assessment title and above the Location/Status row. It flows
  //    in-document as a full-width, left-aligned row below `md`, and is hidden
  //    at `md`+ (desktop keeps the overlay instead).
  //
  // Declared after the base `& button` rule so these overrides win below `md`.
  ...(mobileVariant === 'hideOnMobile' &&
    !fixed &&
    !sticky && {
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    }),

  ...(mobileVariant === 'headerRow' &&
    !fixed &&
    !sticky && {
      // Hidden on desktop — the absolute overlay above covers `md`+.
      [theme.breakpoints.up('md')]: {
        display: 'none',
      },
      [theme.breakpoints.down('md')]: {
        position: 'static',
        top: 'auto',
        right: 'auto',
        zIndex: 'auto',
        display: 'flex',
        justifyContent: 'flex-start',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        padding: theme.spacing(0, 0, 1),
        // `gap` already spaces the buttons; drop the per-button left margin so
        // the first button sits flush against the left edge.
        '& button': {
          marginLeft: 0,
        },
      },
    }),

  '@media print': {
    display: 'none',
  },
}));

export const PageBreak = styled('div', {
  shouldForwardProp: (prop) => prop !== 'first',
})(({ first }) => ({
  position: 'relative',
  width: '100%',
  display: 'block',
  clear: 'both',
  pageBreakInside: 'avoid !important',
  ...(!first && { pageBreakBefore: 'always' }),

  '@media print': {
    marginBottom: 0,
  },
}));

export const SectionToggles = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 4,
  padding: '4px 8px',
  marginTop: theme.spacing(1),
  boxShadow: theme.shadows[2],

  '@media print': {
    display: 'none',
  },
}));

export const SectionToggleLabel = styled(FormControlLabel)({
  '& .MuiFormControlLabel-label': {
    fontSize: 13,
  },
  '& .MuiCheckbox-root': {
    padding: '2px 4px',
  },
});
